import { z } from 'zod';

/**
 * Sandbox Recipe Configuration
 *
 * Validates the structure of sandbox provisioning recipes to prevent injection attacks.
 * Recipes define a series of steps to set up a sandbox environment with test data.
 *
 * Security features:
 * - Validates version format (semver only)
 * - Limits content size to prevent DoS
 * - Caps timeout to max 5 minutes
 * - Uses strict() to reject unknown fields
 * - Validates step types against whitelist
 */
export const SandboxRecipeSchema = z
  .object({
    /**
     * Recipe name (human-readable identifier)
     */
    name: z.string().min(1).max(255),

    /**
     * Semantic version (e.g., "1.0.0")
     * Must match semver format exactly
     */
    version: z.string().regex(/^\d+\.\d+\.\d+$/, 'Version must be in semver format (e.g., 1.0.0)'),

    /**
     * Human-readable description of what this recipe does
     */
    description: z.string().max(1000).optional(),

    /**
     * Ordered list of provisioning steps
     */
    steps: z.array(
      z.object({
        /**
         * Step name (for logging and debugging)
         */
        name: z.string().min(1).max(255),

        /**
         * Step type - determines how the step is executed
         * - sql: Execute SQL statements
         * - seed: Load seed data
         * - migration: Run database migration
         * - fixture: Load fixture files
         * - script: Execute custom script
         */
        type: z.enum(['sql', 'seed', 'migration', 'fixture', 'script']),

        /**
         * File path (relative to recipe directory)
         * Used for migration, fixture, and script types
         */
        file: z.string().max(500).optional(),

        /**
         * Inline content (for sql and seed types)
         * Limited to 50KB to prevent DoS attacks
         */
        content: z.string().max(50000).optional(),

        /**
         * Execution order (optional, defaults to array index)
         */
        order: z.number().int().nonnegative().optional(),
      }),
    ),

    /**
     * Recipe dependencies (must be provisioned first)
     * Array of recipe names that must run before this one
     */
    dependencies: z.array(z.string().max(255)).optional(),

    /**
     * Maximum execution timeout in milliseconds
     * Capped at 5 minutes (300000ms) for security
     */
    timeout: z.number().int().positive().max(300000).optional(),
  })
  .strict(); // Reject any unknown fields for security

/**
 * TypeScript type inferred from SandboxRecipeSchema
 */
export type SandboxRecipe = z.infer<typeof SandboxRecipeSchema>;
